import { createApi } from '@reduxjs/toolkit/query/react';
import { oneBoxV3FetchBase } from '~/api/baseFetch';
import { APIResponseOneBox3 } from '~/api/baseFetch/common.type';
import {
  TOneBoxV3GetResources,
  TOneBoxV3GetResourcesPayload,
  TOneBoxV3UpdateLaunchStatusPayload,
  TOneBoxV3UpdateByFile,
  TOneBoxV3UpdateByFilePayload,
  TOneBoxV3DropDownList,
  TOneBoxV3DropDownListPayload,
  TOneBoxV3UpdateByOriginalFileIdPayload,
  TOneBoxV3GetRecords,
  TOneBoxV3GetRecordsPayload,
  TOneBoxV3GetOwnInfo,
  TOneBoxV3GetInternetResources,
  TOneBoxV3GetInternetResourcesPayload,
  TOneBoxV3InternetDropDownList,
  TOneBoxV3InternetDropDownListPayload,
  TOneBoxV3UpdateInternetByFile,
  TOneBoxV3UpdateInternetByFilePayload,
  TOneBoxV3UpdateInternetByIdPayload,
  TOneBoxV3GetKnowledge,
  TOneBoxV3GetKnowledgePayload,
  TOneBoxV3UpdateByOriginalFileId,
  TOneBoxV3GetResourcesRecommend,
  TOneBoxV3GetResourcesRecommendPayload,
  TOneBoxV3GetRecommendSettings,
  TOneBoxV3UpdateRecommendSettingsPayload,
  TArticlesGetArticlesPayload,
  TArticlesGetArticlesResponse,
  TArticlesGetArticlePayload,
  TArticlesPublicGetArticlesPayload,
  TArticleCreatePayload,
  TArticleUpdatePayload,
  TArticleModelDto,
  TArticleUploadImageResponse,
  TArticleUploadAttachmentResponse,
} from '~/api/oneBoxV3/oneBoxV3.type';

export const oneBoxV3Api = createApi({
  reducerPath: 'oneBoxV3Api',
  baseQuery: oneBoxV3FetchBase,
  tagTypes: ['Resources', 'InternetResources', 'RecommendSettings', 'Articles'],
  endpoints: builder => ({
    getOwnInfo: builder.query<APIResponseOneBox3<TOneBoxV3GetOwnInfo>, void>({
      query: () => ({
        url: '/v1/users/getOwnInfo',
      }),
    }),
    getResources: builder.query<
      APIResponseOneBox3<TOneBoxV3GetResources>,
      TOneBoxV3GetResourcesPayload
    >({
      query: data => ({
        url: '/v1/resources/getResources',
        body: data,
      }),
      extraOptions: {
        forceRefetch: true, // 強制重新獲取數據
      },
      providesTags: ['Resources'],
    }),
    updateLaunchStatus: builder.mutation<
      APIResponseOneBox3<null>,
      TOneBoxV3UpdateLaunchStatusPayload
    >({
      query: data => ({
        url: '/v1/resources/updateLaunchStatus',
        body: data,
      }),
      invalidatesTags: ['Resources'],
    }),
    updateByFile: builder.mutation<
      APIResponseOneBox3<TOneBoxV3UpdateByFile>,
      TOneBoxV3UpdateByFilePayload
    >({
      query: data => ({
        url: '/v1/resources/updateResources/byFile',
        body: data,
      }),
      invalidatesTags: ['Resources'],
    }),
    updateByOriginalFileId: builder.mutation<
      APIResponseOneBox3<TOneBoxV3UpdateByOriginalFileId>,
      TOneBoxV3UpdateByOriginalFileIdPayload
    >({
      query: data => ({
        url: '/v1/resources/updateResource/byOriginalFileId',
        body: data,
      }),
      invalidatesTags: ['Resources'],
    }),
    dropDownList: builder.query<
      APIResponseOneBox3<TOneBoxV3DropDownList>,
      TOneBoxV3DropDownListPayload
    >({
      query: data => ({
        url: '/v1/resources/getResources/dropDownList',
        body: data,
      }),
      extraOptions: {
        forceRefetch: true, // 強制重新獲取數據
      },
    }),
    getRecords: builder.query<
      APIResponseOneBox3<TOneBoxV3GetRecords>,
      TOneBoxV3GetRecordsPayload
    >({
      query: data => ({
        url: '/v1/records/getRecords',
        body: data,
      }),
    }),
    getInternetResources: builder.query<
      APIResponseOneBox3<TOneBoxV3GetInternetResources>,
      TOneBoxV3GetInternetResourcesPayload
    >({
      query: data => ({
        url: '/v1/internetResources/getInternetResources',
        body: data,
      }),
      providesTags: ['InternetResources'],
    }),
    updateInternetLaunchStatus: builder.mutation<
      APIResponseOneBox3<null>,
      TOneBoxV3UpdateLaunchStatusPayload
    >({
      query: data => ({
        url: '/v1/internetResources/updateLaunchStatus',
        body: data,
      }),
      invalidatesTags: ['InternetResources'],
    }),
    InternetDropDownList: builder.query<
      APIResponseOneBox3<TOneBoxV3InternetDropDownList>,
      TOneBoxV3InternetDropDownListPayload
    >({
      query: data => ({
        url: '/v1/internetResources/getInternetResources/dropDownList',
        body: data,
      }),
    }),
    updateInternetByFile: builder.mutation<
      APIResponseOneBox3<TOneBoxV3UpdateInternetByFile>,
      TOneBoxV3UpdateInternetByFilePayload
    >({
      query: data => ({
        url: '/v1/internetResources/update/byFile',
        body: data,
      }),
      invalidatesTags: ['InternetResources'],
    }),
    updateInternetById: builder.mutation<
      APIResponseOneBox3<null>,
      TOneBoxV3UpdateInternetByIdPayload
    >({
      query: data => ({
        url: '/v1/internetResources/update/byId',
        body: data,
      }),
      invalidatesTags: ['InternetResources'],
    }),
    getKnowledge: builder.query<
      APIResponseOneBox3<TOneBoxV3GetKnowledge>,
      TOneBoxV3GetKnowledgePayload
    >({
      query: data => ({
        url: '/v1/resources/getKnowledge',
        body: data,
      }),
    }),
    getResourcesRecommend: builder.query<
      APIResponseOneBox3<TOneBoxV3GetResourcesRecommend>,
      TOneBoxV3GetResourcesRecommendPayload
    >({
      query: data => ({
        url: '/v1/resources/getResources/recommend',
        body: data,
      }),
    }),
    getRecommendSettings: builder.query<
      APIResponseOneBox3<TOneBoxV3GetRecommendSettings>,
      void
    >({
      query: data => ({
        url: '/v1/recommendSettings/get',
        body: data,
      }),
      providesTags: result => {
        return result ? ['RecommendSettings'] : [];
      },
    }),
    updateRecommendSettings: builder.mutation<
      APIResponseOneBox3<null>,
      TOneBoxV3UpdateRecommendSettingsPayload
    >({
      query: data => ({
        url: '/v1/recommendSettings/update',
        body: data,
      }),
      invalidatesTags: (result, data, payload) => {
        return result && !payload.weekAdjust ? ['RecommendSettings'] : [];
      },
    }),
    articlesGetArticles: builder.mutation<
      APIResponseOneBox3<TArticlesGetArticlesResponse>,
      TArticlesGetArticlesPayload
    >({
      query: body => ({
        url: '/v1/articles/getArticles',
        body,
      }),
    }),
    articlesGetArticle: builder.mutation<
      APIResponseOneBox3<TArticleModelDto>,
      TArticlesGetArticlePayload
    >({
      query: body => ({
        url: '/v1/articles/getArticle',
        body,
      }),
    }),
    articlesPublicGetArticles: builder.mutation<
      APIResponseOneBox3<TArticlesGetArticlesResponse>,
      TArticlesPublicGetArticlesPayload
    >({
      query: body => ({
        url: '/v1/articles/public/getArticles',
        body,
      }),
    }),
    articlesCreate: builder.mutation<
      APIResponseOneBox3<TArticleModelDto>,
      TArticleCreatePayload
    >({
      query: body => ({
        url: '/v1/articles/create',
        body,
      }),
      invalidatesTags: ['Articles'],
    }),
    articlesUpdate: builder.mutation<
      APIResponseOneBox3<TArticleModelDto>,
      TArticleUpdatePayload
    >({
      query: body => ({
        url: '/v1/articles/update',
        body,
      }),
      invalidatesTags: ['Articles'],
    }),
    articlesUploadImage: builder.mutation<
      APIResponseOneBox3<TArticleUploadImageResponse>,
      FormData
    >({
      query: body => ({
        url: '/v1/articles/uploadImage',
        body,
      }),
    }),
    articlesUploadAttachment: builder.mutation<
      APIResponseOneBox3<TArticleUploadAttachmentResponse>,
      FormData
    >({
      query: body => ({
        url: '/v1/articles/uploadAttachment',
        body,
      }),
    }),
  }),
});

export const {
  useLazyGetOwnInfoQuery,
  useGetResourcesQuery,
  useLazyGetResourcesQuery,
  useUpdateLaunchStatusMutation,
  useUpdateByFileMutation,
  useUpdateByOriginalFileIdMutation,
  useDropDownListQuery,
  useLazyDropDownListQuery,
  useGetRecordsQuery,
  useLazyGetRecordsQuery,
  useGetInternetResourcesQuery,
  useLazyGetInternetResourcesQuery,
  useInternetDropDownListQuery,
  useLazyInternetDropDownListQuery,
  useUpdateInternetLaunchStatusMutation,
  useUpdateInternetByFileMutation,
  useUpdateInternetByIdMutation,
  useGetKnowledgeQuery,
  useGetResourcesRecommendQuery,
  // useGetRecommendSettingsQuery,
  useLazyGetRecommendSettingsQuery,
  useUpdateRecommendSettingsMutation,
  useArticlesGetArticlesMutation,
  useArticlesGetArticleMutation,
  useArticlesPublicGetArticlesMutation,
  useArticlesCreateMutation,
  useArticlesUpdateMutation,
  useArticlesUploadImageMutation,
  useArticlesUploadAttachmentMutation,
} = oneBoxV3Api;
